import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api, ApiError } from '../../api/client'
import { useSession } from '../../store/user.store'
import { Button } from '../../components/ui/Button'
import { LoadingDots } from '../../components/ui/LoadingDots'
import { SectionedMarkdown } from '../../components/ui/SectionedMarkdown'
import { SectionHeader } from '../../components/ui/SectionHeader'
import { PageHeader } from '../../components/ui/PageHeader'
import { usePaywallStore } from '../../store/paywall.store'
import { BirthModal } from './birth-modal'
import { formatWeekRange } from '../../lib/date-format'

export function AstroWeeklyRoute() {
  const { t } = useTranslation()
  const user = useSession((s) => s.user)
  const showPaywall = usePaywallStore((s) => s.show)
  const qc = useQueryClient()
  const [birthOpen, setBirthOpen] = useState(false)

  const weeklyCachedQuery = useQuery({
    queryKey: ['forecast', 'weekly', 'cached'],
    queryFn: () => api.weeklyForecastCurrent(),
    enabled: Boolean(user?.birthDate),
    staleTime: 1000 * 60 * 60,
  })

  const weeklyMutation = useMutation({
    mutationFn: () => api.weeklyForecast(),
    onSuccess: (data) => {
      qc.setQueryData(['forecast', 'weekly', 'cached'], data)
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 402) {
        const detail = err.details
        showPaywall({
          reason: 'feature_locked',
          feature: (detail?.feature as string | undefined) ?? 'forecast_weekly',
        })
      }
    },
  })

  const weekly = weeklyCachedQuery.data
  const lang = (user?.language ?? 'ru') as 'ru' | 'en'

  if (!user?.birthDate) {
    return (
      <div className="flex flex-col gap-5 pb-6">
        <PageHeader title={t('forecast.weekly_title')} backTo="/astro" />
        <p className="text-sage-700">{t('natal.no_birth')}</p>
        <Button size="lg" onClick={() => setBirthOpen(true)}>{t('natal.set_birth')}</Button>
        <BirthModal open={birthOpen} onClose={() => setBirthOpen(false)} onSaved={() => qc.invalidateQueries()} />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader title={t('forecast.weekly_title')} backTo="/astro" />

      {!weekly ? (
        <section className="flex flex-col gap-3 rounded-3xl bg-white/60 p-5 ring-1 ring-sage-200/60">
          <h2 className="font-serif text-xl text-sage-800">{t('forecast.weekly_cta')}</h2>
          {weeklyCachedQuery.isLoading && <LoadingDots compact />}
          {!weeklyCachedQuery.isLoading && (
            <Button
              size="lg"
              loading={weeklyMutation.isPending}
              onClick={() => weeklyMutation.mutate()}
            >
              {t('forecast.weekly_open')}
            </Button>
          )}
        </section>
      ) : (
        <section className="flex flex-col gap-3">
          <SectionHeader
            eyebrow={formatWeekRange(weekly.periodStart, weekly.periodEnd, lang)}
            title={t('forecast.weekly_title')}
          />
          <SectionedMarkdown>{weekly.interpretation}</SectionedMarkdown>
        </section>
      )}
    </div>
  )
}
